import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import NextLink from 'next/link';
import Image from 'next/image';
import { useI18n } from 'next-localization';
import styles from './TeamProfileCard.module.scss';
import IconChevronQuickLinks from 'components/Generic/Icons/IconChevronQuickLinks';
import TeamProfilePlaceholder from 'components/Generic/Icons/TeamProfilePlaceholder';
import { TeamMember } from 'model/TeamMember';

type Props = {
  fields: TeamMember;
  showImage: boolean;
  className: string;
};

const TeamProfileCard = ({ fields, showImage = true, className }: Props) => {
  const { t } = useI18n();

  if (!showImage) {
    return null;
  }

  const fullName = { value: fields?.firstName + ' ' + fields?.lastName };
  const haveImage = !!fields?.image?.url;
  const haveEmail = !!fields?.email;
  const havePhone = !!fields?.phoneNumber;
  const haveLink = !!fields?.biographyPageUrl;

  return (
    <div className={`${styles.teamProfileCard__thumbnail} ${className}`}>
      {haveImage ? (
        <Image
          src={fields.image.url}
          alt={fields.image.alt}
          width={270}
          height={270}
          style={{ objectFit: 'cover', width: '100%', height: '270px' }}
        />
      ) : (
        <div
          className="background-black w-100 d-flex align-items-center justify-content-center object-fit-cover"
          style={{ height: '270px' }}
        >
          <TeamProfilePlaceholder className="text-white" />
        </div>
      )}
      <div className="d-flex flex-column justify-content-between py-3 px-2 flex-grow-1 background-digital-light-grey">
        <div className="d-flex flex-column">
          <Text tag="h5" className={`${styles.teamProfileCard__title} mb-2`} field={fullName} />
          <Text
            tag="span"
            className="section-header-small mb-2"
            field={{ value: fields.jobTitle }}
          />
          {havePhone && (
            <a href={`tel:${fields.phoneNumber}`} className="d-flex align-items-center mb-2 fs-4">
              <i className={`bi bi-telephone ${styles.icon} me-1`} aria-hidden={true} />
              <Text
                tag="span"
                className="section-header-small text-break"
                field={{ value: fields.phoneNumber }}
              />
            </a>
          )}
          {haveEmail && (
            <a href={`mailto:${fields.email}`} className="d-flex align-items-center mb-2 fs-4">
              <i
                className={`bi bi-envelope flex-shrink-0 ${styles.icon} me-1`}
                aria-hidden={true}
              />
              <Text
                tag="span"
                className="section-header-small text-lowercase text-break"
                field={{ value: fields.email }}
              />
            </a>
          )}
        </div>

        {haveLink && (
          <NextLink
            href={fields.biographyPageUrl}
            className="d-flex align-items-center flex-shrink-0 p-0 text-link h-auto"
            prefetch={false}
          >
            <span>{t('readBiography')}</span>
            <IconChevronQuickLinks className="ms-2" />
          </NextLink>
        )}
      </div>
    </div>
  );
};

export default TeamProfileCard;
